import { Group, Stack, Text, Tooltip, VisuallyHidden } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Languages, Users, Video, Volume1, VolumeX } from "lucide-react";
import { useState } from "react";

import type { DubbingCaps } from "../../../api/generated/model";
import { VideoDubQuote, VideoDubRequest } from "../../../api/generated/zod";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { sourceLimits, sourceProblem } from "../../../platform/media/video-source";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Pill } from "../../../ui/Pill";
import {
  Errors,
  ROUTE,
  StartGroup,
  VideoSlot,
  defaultModel,
  rowsOf,
  type ToolFormProps,
} from "./form-parts";
import { useToolVideo } from "./use-tool-video";
import { videoLimitLines } from "../video/limits";

/** Live's tooltips and words (`video-dubbing-form/constants.ts`, `drop-background-audio-toggle`). */
const SOURCE_TIP = "Source language";
const TARGET_TIP = "Target language";
const SPEAKERS_TIP = "Number of speakers";
const BACKGROUND_TIP = "Background audio";
const NO_BACKGROUND = "This model does not support removing background audio";
/** Live's "Auto" speakers, sent as 0: ElevenLabs counts them. */
const AUTO = "0";

/** The language a model offers, else its default (live's model defaults keep a language still offered). */
function offered(code: string | null, codes: readonly string[], fallback: string): string {
  return code !== null && codes.includes(code) ? code : fallback;
}

/** Live's pair rule: a target equal to a (not auto) source moves to the first other language. */
function pairedTarget(caps: DubbingCaps, source: string, target: string): string {
  if (source === "auto" || source !== target) return target;
  return caps.target_languages.find((each) => each.code !== source)?.code ?? target;
}

/**
 * Live's Video Dubbing form (#215, `video-dubbing-form`, capture 11, actions-raw 11): the "Video"
 * slot and no prompt; the model; the source language ("Detected (auto)" first) and the target
 * (Spanish), a target equal to the source greyed out; the speakers (Auto, then one to the
 * model's most) where the model counts them; live's background chip, "Off" by default; the
 * price and the start. The chip reads as live's does: "On" drops the background audio.
 */
export function DubbingForm({ catalog, above, overlay, onStarted, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.dub");
  const [modelId, setModelId] = useState(() => defaultModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const caps = model.modes["video.dub"]?.dubbing ?? null;
  const [keptSource, setSource] = useState<string | null>(null);
  const [keptTarget, setTarget] = useState<string | null>(null);
  const [keptSpeakers, setSpeakers] = useState(AUTO);
  const [keptDrop, setDrop] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const tool = useToolVideo(teamId, brandId);
  // Live's model defaults: what the model still offers stays, the rest takes its defaults.
  const sources = caps?.source_languages ?? [];
  const targets = caps?.target_languages ?? [];
  const source = caps === null ? null : offered(keptSource, sources.map((each) => each.code), caps.default_source);
  const target =
    caps === null || source === null
      ? null
      : pairedTarget(caps, source, offered(keptTarget, targets.map((each) => each.code), caps.default_target));
  const most = caps?.max_speakers ?? null;
  const speakers = most !== null && Number(keptSpeakers) <= most ? keptSpeakers : AUTO;
  const drops = caps?.drop_background_audio === true;
  const drop = drops && keptDrop;
  // The model's source limits, ours (600 s, 200 MB; G7-video-dubbing 6): a longer or bigger
  // video is said under the slot and never sent (the start refuses it too).
  const problem = caps === null ? null : sourceProblem(caps.source, tool.video);
  const fields = {
    type: "video.dub" as const,
    model: model.id,
    ...(tool.ref === null || problem !== null ? {} : { source_video: tool.ref }),
    ...(source === null ? {} : { source_lang: source }),
    ...(target === null ? {} : { target_lang: target }),
    ...(most === null ? {} : { num_speakers: Number(speakers) }),
    ...(drops ? { drop_background_audio: drop } : {}),
  };
  const quote = VideoDubQuote.safeParse(fields);
  const request = VideoDubRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const chooseSource = (next: string) => {
    setSource(next);
    if (caps !== null && target !== null) setTarget(pairedTarget(caps, next, target));
  };
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={
        <Stack gap={6}>
          <VideoSlot tool={tool} limits={caps === null ? [] : videoLimitLines(sourceLimits(caps.source))} />
          {problem === null ? null : (
            <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
              {problem}
            </Text>
          )}
        </Stack>
      }
      summary={<span>Video Dubbing</span>}
      above={above}
      overlay={overlay}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ModelPicker models={models} value={model.id} onChange={setModelId} />
            {source === null ? null : (
              <Choice
                setting="Source language"
                icon={<Video size={20} strokeWidth={1.5} />}
                chevron={false}
                width={150}
                tip={SOURCE_TIP}
                value={source}
                options={sources.map((each) => ({ value: each.code, label: each.name }))}
                onChange={chooseSource}
              />
            )}
            {target === null ? null : (
              <Choice
                setting="Target language"
                icon={<Languages size={20} strokeWidth={1.5} />}
                chevron={false}
                width={130}
                tip={TARGET_TIP}
                value={target}
                options={targets.map((each) => ({
                  value: each.code,
                  label: each.name,
                  disabled: source !== "auto" && each.code === source,
                }))}
                onChange={setTarget}
              />
            )}
            {most === null ? null : (
              <Choice
                setting="Speakers"
                icon={<Users size={20} strokeWidth={1.5} />}
                chevron={false}
                tip={SPEAKERS_TIP}
                value={speakers}
                options={[
                  { value: AUTO, label: "Auto" },
                  ...Array.from({ length: most }, (_, at) => ({ value: String(at + 1), label: String(at + 1) })),
                ]}
                onChange={setSpeakers}
              />
            )}
            {drops ? (
              <Tooltip label={BACKGROUND_TIP} withArrow>
                <Pill
                  label={drop ? "On" : "Off"}
                  icon={<Volume1 size={20} strokeWidth={1.5} />}
                  active={drop}
                  aria-label={BACKGROUND_TIP}
                  aria-pressed={drop}
                  onClick={() => setDrop(!drop)}
                />
              </Tooltip>
            ) : (
              <Tooltip label={NO_BACKGROUND} withArrow>
                <span>
                  <VolumeX size={20} strokeWidth={1.5} aria-hidden />
                  <VisuallyHidden>{NO_BACKGROUND}</VisuallyHidden>
                </span>
              </Tooltip>
            )}
          </Group>
          <StartGroup job={job} notes={model.modes["video.dub"]?.notes ?? []} />
        </Group>
      }
    >
      <Errors job={job} uploads={[tool.upload]} />
      {tool.dialog}
    </GlassPanel>
  );
}
